import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { sparkAssetUrl } from '../../utils/sparkAssetUrl';
import './ButtonProvider.css';

/** Identity providers with a built-in brand logo. */
export type ButtonProviderName = 'google' | 'microsoft' | 'okta';

export const BUTTON_PROVIDER_NAMES: ButtonProviderName[] = ['google', 'microsoft', 'okta'];

const PROVIDER_LOGOS: Record<ButtonProviderName, string> = {
  google: 'logo/providers/google.png',
  microsoft: 'logo/providers/microsoft.png',
  okta: 'logo/providers/okta.png',
};

export type ButtonProviderProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  /** Identity provider — renders the matching brand logo inside the icon bubble. */
  provider?: ButtonProviderName;
  /** Custom icon node. Overrides the built-in `provider` logo when supplied. */
  icon?: ReactNode;
  /** Button label, e.g. "Sign in with Google". */
  children: ReactNode;
  /** Stretch to the full width of the container. Defaults to `true` (login card layout). */
  fill?: boolean;
};

/**
 * Large sign-in button for third-party identity providers (Figma "Login" buttons):
 * white surface, 1px default border, 16px radius, 60px tall, with a round neutral
 * icon bubble on the left of a Base / Semibold label.
 */
export const ButtonProvider = forwardRef<HTMLButtonElement, ButtonProviderProps>(
  function ButtonProvider(
    { provider, icon, children, fill = true, className = '', type = 'button', ...rest },
    ref,
  ) {
    const rootClass = [
      'spark-btn-provider',
      fill ? 'spark-btn-provider-fill' : '',
      provider ? `spark-btn-provider-${provider}` : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const iconNode =
      icon ??
      (provider ? (
        <img
          className="spark-btn-provider-logo"
          src={sparkAssetUrl(PROVIDER_LOGOS[provider])}
          alt=""
          aria-hidden="true"
        />
      ) : null);

    return (
      <button ref={ref} type={type} className={rootClass} {...rest}>
        {iconNode && (
          <span className="spark-btn-provider-icon" aria-hidden="true">
            {iconNode}
          </span>
        )}
        <span className="spark-btn-provider-label spark-text-base-semibold">{children}</span>
      </button>
    );
  },
);

ButtonProvider.displayName = 'ButtonProvider';
